import React, { useState } from 'react';
import styles from './sections.module.css';
import { publicSiteAPI } from '../../../services/api';
import { useScrollReveal } from '../hooks/useScrollReveal';

interface ContactSectionProps {
  project: any;
  slug: string;
}

export const ContactSection: React.FC<ContactSectionProps> = ({ project, slug }) => {
  const ref = useScrollReveal();
  const pw = project.publicWebsite;
  const contact = pw?.contact || {};
  const person = contact.contactPerson || {};

  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    setError(null);
    try {
      await publicSiteAPI.submitContact(slug, form);
      setSent(true);
      setForm({ name: '', email: '', subject: '', message: '' });
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Er ging iets mis. Probeer het later opnieuw.');
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className={styles.section} ref={ref}>
      <div className={styles.container}>
        <h2 className={styles.sectionTitle}>{contact.heading || 'Neem contact met ons op'}</h2>
        {contact.description && <p className={styles.sectionSubtitle}>{contact.description}</p>}

        {contact.image && (
          <div className={styles.sectionImage}>
            <img src={contact.image} alt="Contact" />
          </div>
        )}
        <div className={styles.contactGrid}>
          {(person.name || person.email || person.phone) && (
            <div className={styles.contactInfo}>
              <div className={styles.infoCard}>
                <h4>Contactpersoon</h4>
                {person.name && (
                  <div className={styles.contactPerson}>
                    <strong>{person.name}</strong>
                    {person.role && <span>{person.role}</span>}
                  </div>
                )}
                {person.email && (
                  <a href={`mailto:${person.email}`} className={styles.contactLink}>
                    {person.email}
                  </a>
                )}
                {person.phone && (
                  <a href={`tel:${person.phone}`} className={styles.contactLink}>
                    {person.phone}
                  </a>
                )}
              </div>
            </div>
          )}

          {contact.showForm !== false && (
            <div className={styles.contactForm}>
              {sent ? (
                <div className={styles.formSuccess}>
                  <h3>Bedankt voor uw bericht!</h3>
                  <p>We nemen zo snel mogelijk contact met u op.</p>
                  <button
                    className={styles.formButton}
                    onClick={() => setSent(false)}
                  >
                    Nog een bericht sturen
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <div className={styles.formRow}>
                    <div className={styles.formGroup}>
                      <label>Naam *</label>
                      <input
                        type="text"
                        value={form.name}
                        onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
                        required
                      />
                    </div>
                    <div className={styles.formGroup}>
                      <label>E-mail *</label>
                      <input
                        type="email"
                        value={form.email}
                        onChange={e => setForm(prev => ({ ...prev, email: e.target.value }))}
                        required
                      />
                    </div>
                  </div>
                  <div className={styles.formGroup}>
                    <label>Onderwerp</label>
                    <input
                      type="text"
                      value={form.subject}
                      onChange={e => setForm(prev => ({ ...prev, subject: e.target.value }))}
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label>Bericht *</label>
                    <textarea
                      value={form.message}
                      onChange={e => setForm(prev => ({ ...prev, message: e.target.value }))}
                      rows={5}
                      required
                      minLength={10}
                    />
                  </div>
                  {error && <p className={styles.formError}>{error}</p>}
                  <button
                    type="submit"
                    className={styles.formButton}
                    disabled={sending}
                  >
                    {sending ? 'Verzenden...' : 'Verstuur bericht'}
                  </button>
                </form>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
